import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Modal, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import router from "../../../../router";
import store from "../../../../store";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has, have } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionOpenUrl,
    createRowActionReStatus,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { checkUsedInvoices, isStringNumber, required, requiredWhen, validateError } from "../../../../utils/validate";
import { openModalForm as openModalFormConfirmApprove } from "../../storage-location/request/modal/confirm-approve.logic";
import { openModalForm as openModalFormConfirmRequest } from "../../storage-location/request/modal/confirm-request.logic";
import { callRequestDoc, callRequestStatus, callRequestType } from "../../storage-location/request/request.section";
import {
    callRequestDocGenType,
    callUser,
    callUserCompanyMe,
    checkUserCompanyOperation
} from "../../storage-location/request/requestor/requestor.section";
import { createForm as createFormInform } from "../inform/inform.logic";
import { openModalForm as openModalFormConfirmReceive } from "./modal/confirm-receive.logic";
import { createConditionAction, createConditionForm } from "./request.condition";
import {
    callImportSampleAttachment,
    callInformAttachment,
    callInformProductQtyMargin,
    callInspectionLv,
    callPassAttachment,
    callUserObjective,
    callUserObjective3,
    createColumns,
    createField,
    requiredNetWeight,
    requiredQuantityRemaining
} from "./request.section";
import { formatXml } from "../../../../utils/util";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();
export const actionPrefix = "resource/v1/transaction/request";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReStatus = ({ data }) => store.dispatch(`${actionPrefix}/reStatus`, { data });
export const callReportInform = ({ data }) => store.dispatch(`${actionPrefix}/reportInform`, { data });
export const callSendNsw = ({ data }) => store.dispatch(`${actionPrefix}/sendNsw`, { data });

export const onSearch = ({ props, page, state }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refInformTemp.refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refInformTemp.refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refInformTemp.refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const createWhereFor = ({ props }) => {
    const { menuContext, requestDocKeys } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "createBy", type: "String", operator: "=", value: userInfo.username }]);
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refInformTemp.refUser.username", type: "String", operator: "=", value: userInfo.username }]);
        if (userInfo.isCeo) {
            whereGroup.push([
                { name: "refInformTemp.refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id }
            ]);
        }
    }
    if (has({ menuContext, key: "forReceive" })) {
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id }
        ]);
    }
    if (has({ menuContext, key: "forApprove" })) {
        whereGroup.push([{ name: "refToApproveBy.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (has({ menuContext, key: "forCustoms" })) {
        const departmentIds = ["baf5fc2f-f910-4d1d-a824-9fee2961fab3"]
        if (userInfo.refDepartment?.id && userInfo.refDepartment?.id !== "baf5fc2f-f910-4d1d-a824-9fee2961fab3") {
            departmentIds.push(userInfo.refDepartment?.id);
        }
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "in", value: ["approve", "notApprove", "cancel", "waitReleaseResult", "waitRelease", "waitCompanyAcknowledge"] },
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id },
        ])
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "in", value: ["approve", "notApprove", "cancel", "waitReleaseResult", "waitRelease", "waitCompanyAcknowledge"] },
            { name: "refDepartment.id", type: "UUID", operator: "in", value: departmentIds },
            { openBucket: true, name: "refInformTemp.refImportCheckpoint.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id },
            { logic: "OR", name: "refInformTemp.refExportCheckpoint.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id },
            { logic: "OR", name: "refInformTemp.refImportCheckpoint2.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id },
            { closeBucket: true, logic: "OR", name: "refInformTemp.refExportCheckpoint2.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id },
        ])
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    return [
        { name: "refRequestDoc.key", type: "String", operator: "in", value: requestDocKeys },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createSearchOneCriteria = ({ props, id, forLog }) => {
    if (!forLog) {
        return {
            select: [
                "**",
                "refInform.id",
                "refInform.approveCode",
                "refInform.effectiveDate",
                "refInform.expireDate",
                "refInform.refUserCompany.*",
                "refInform.refUserCompany.refPersonType.*",
                "refInformTemp.**",
                "refInformTemp.refUserCompany.**",
                "refInformTemp.refUser.**",
                "refInformTemp.refHazObject.**",
                "refInformTemp.refTransferDeclaration.*"
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props })],
            children: [
                {
                    name: "refInformTemp.refUserCompany.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refInformTemp.refUserCompany.userObjectives",
                    select: ["*", "refObjective.*"],
                    page: 0,
                    size: 1000000
                },
                {
                    name: "refInformTemp.refUser.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.draft",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "draft" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitReceive",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitApprove",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitApprove" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "feeStatements",
                    select: ["*", "refType.*", "refStatus.*"],
                    page: 0,
                    size: 1,
                    sort: [{ name: "createDate", type: "DESC" }],
                    children: []
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const onClickSave = ({ props, data, refs, state, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props;
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs, state });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const validate = ({ props, data, refs }) => {
    const { menuContext } = props;
    const rulesTop = createRulesTop({ props, data, refs });
    const rules1 = createRulesTab1({ props, data, refs });
    const rules2 = createRulesTab2({ props, data, refs });
    const rules3 = createRulesTab3({ props, data, refs });
    const rules4 = createRulesTab4({ props, data, refs });
    const rules5 = createRulesTab5({ props, data, refs });
    let tabKey = null;
    return data.useForm
        .validate(Object.keys(rulesTop))
        .then(() => {
            tabKey = "Requestor";
            return data.useForm.validate(Object.keys(rules1));
        })
        .then(() => {
            tabKey = "InformInfo";
            return data.useForm.validate(Object.keys(rules2));
        })
        .then(() => {
            tabKey = "InformInfo2";
            return data.useForm.validate(Object.keys(rules3));
        })
        .then(() => {
            tabKey = "InformProduct";
            return data.useForm.validate(Object.keys(rules4));
        })
        .then(() => {
            tabKey = "Attachment";
            return data.useForm.validate(Object.keys(rules5));
        })
        .catch(error => {
            if (tabKey) {
                menuContext.tabKey = tabKey;
            }
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refRequestDoc?.value} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        draftCode: null,
        requestCode: null,
        refDepartment: null,
        isSignCa: null,
        refInform: null,
        refInformTemp: { ...createFormInform() },
        lastStatus: {
            draft: [{ remark: null, statusDatetime: null }],
            waitReceive: [{ remark: null, statusDatetime: null }],
            waitApprove: [{ remark: null, statusDatetime: null }],
            approve: [{ remark: null, statusDatetime: null }],
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forReceiveRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    return {
        ...createRulesTop({ props, data, refs, key }),
        ...createRulesTab1({ props, data, refs, key }),
        ...createRulesTab2({ props, data, refs, key }),
        ...createRulesTab3({ props, data, refs, key }),
        ...createRulesTab4({ props, data, refs, key }),
        ...createRulesTab5({ props, data, refs, key })
    };
};

export const createRulesTop = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDocGenType`]: [required],
        [`${prefix}refRequestType`]: [required],
        // [`${prefix}requestCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        // [`${prefix}refInformTemp.approveCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refInform`]: [requiredWhen({ when: () => ["2", "3", "4"].includes(model.refRequestType?.key) })],
        [`${prefix}refInformTemp.refOperation`]: [required],
        [`${prefix}refInformTemp.effectiveDate`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refInformTemp.approveManualDate`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && ["2", "4"].includes(model.refRequestType?.key) && (!model.id || ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesTab1 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDepartment`]: [required],
        [`${prefix}refInformTemp.refUserCompany`]: [
            requiredWhen({ when: () => true, requiredValidator: () => model.refInformTemp.refUserCompany?.id })
        ],
        [`${prefix}refInformTemp.refUser`]: [requiredWhen({ when: () => true, requiredValidator: () => model.refInformTemp.refUser?.id })]
    };
};

export const createRulesTab2 = ({ props, data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refInformTemp.importExportDate`]: [required],
        [`${prefix}refInformTemp.refTransportByWay`]: [required],
        [`${prefix}refInformTemp.refTransportObjective`]: [
            requiredWhen({ when: () => ["16", "17", "18", "19"].some(d => props.requestDocKeys.includes(d)) })
        ],
        [`${prefix}refInformTemp.vehicleNo`]: [required],
        [`${prefix}refInformTemp.refOriginDestinationCountry`]: [required],
        [`${prefix}refInformTemp.refImportCheckpoint`]: [required],
        [`${prefix}refInformTemp.refExportCheckpoint`]: [required],
        [`${prefix}refInformTemp.refPassCustoms`]: [required],
        [`${prefix}refInformTemp.sealNo`]: [requiredWhen({ when: () => {
            return ["16", "17", "18", "19"].includes(model.refRequestDoc?.key) && (!model.createDate || moment(model.createDate).isAfter(moment("2024-12-12T17:00:00:000000Z")))
        }})],
        [`${prefix}refInformTemp.containerNo`]: [requiredWhen({ when: () => {
            return ["16", "17", "18", "19"].includes(model.refRequestDoc?.key) && (!model.createDate || moment(model.createDate).isAfter(moment("2024-12-12T17:00:00:000000Z")))
        }})],
        [`${prefix}refInformTemp.sealNo2`]: [requiredWhen({ when: () => {
            return ["18", "19"].includes(model.refRequestDoc?.key) && (!model.createDate || moment(model.createDate).isAfter(moment("2024-12-12T17:00:00:000000Z")))
        }})],
        [`${prefix}refInformTemp.containerNo2`]: [requiredWhen({ when: () => {
            return ["18", "19"].includes(model.refRequestDoc?.key) && (!model.createDate || moment(model.createDate).isAfter(moment("2024-12-12T17:00:00:000000Z")))
        }})],
        [`${prefix}refInformTemp.refTransferDeclaration`]: [
            {
                validator: () => {
                    if (model.refStatus?.key === 'draft' && ["2"].includes(modelRef.refRequestType?.key)) {
                        if (model.refInformTemp.refTransferDeclaration?.createDate) {
                            return Promise.reject(i18n.global.t("error.form.cannotChange"));
                        }
                    }
                    return Promise.resolve();
                },
                required: false,
                trigger: "change"
            }
        ]
    };
    return rules;
};

export const createRulesTab3 = ({ props, key }) => {
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refInformTemp.importExportDate2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })],
        [`${prefix}refInformTemp.refTransportByWay2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })],
        /*
        [`${prefix}refInformTemp.refTransportObjective2`]: [
            requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })
        ],
        */
        [`${prefix}refInformTemp.vehicleNo2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })],
        //[`${prefix}refInformTemp.containerNo2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })],
        //[`${prefix}refInformTemp.sealNo2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })],
        [`${prefix}refInformTemp.refOriginDestinationCountry2`]: [
            requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })
        ],
        [`${prefix}refInformTemp.refImportCheckpoint2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })],
        [`${prefix}refInformTemp.refExportCheckpoint2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })],
        [`${prefix}refInformTemp.refPassCustoms2`]: [requiredWhen({ when: () => ["18", "19"].some(d => props.requestDocKeys.includes(d)) })]
    };
    return rules;
};

export const createRulesTab4 = ({ props, data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const initialModel = data.useForm?.initialModel || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const initModel = key ? lodash.get(initialModel, key) : initialModel;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refInformTemp.products`]: [required]
    };
    model?.refInformTemp?.products?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refInformTemp.products.${index}.refHazSpecies`] = [
                requiredWhen({ when: () => ["16", "17", "18", "19"].some(d => props.requestDocKeys.includes(d)) })
            ];
            rules[`${prefix}refInformTemp.products.${index}.invoiceCode`] = [required];
            rules[`${prefix}refInformTemp.products.${index}.invoiceDate`] = [required];
            rules[`${prefix}refInformTemp.products.${index}.invoiceSeq`] = props.requestDocKeys?.[0] === "02" ? [required, isStringNumber] : [required, isStringNumber,
                checkUsedInvoices({
                    dataFunc: () => ({
                        requestTypeKey: model.refRequestType?.key,
                        userCompanyId: model.refInformTemp?.refUserCompany?.id,
                        invoiceCode: model.refInformTemp?.products?.find(d => d.id === item.id || d._id === item._id)?.invoiceCode,
                        invoiceDate: model.refInformTemp?.products?.find(d => d.id === item.id || d._id === item._id)?.invoiceDate,
                        invoiceSeq: model.refInformTemp?.products?.find(d => d.id === item.id || d._id === item._id)?.invoiceSeq,
                        excludeInformTempId: model.refInformTemp?.id,
                        excludeApproveCode: model.refInform?.approveCode,
                        createDate: model?.createDate
                    })
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.refHazObject`] = [
                requiredWhen({
                    when: () =>
                        (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                        ["02", "18", "19"].some(d => props.requestDocKeys.includes(d))
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.refHaz`] = [
                requiredWhen({
                    when: () => ["16", "17"].some(d => props.requestDocKeys.includes(d)) && ["2", "3"].includes(item.refHazSpecies?.key)
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.refOriginDestinationCountry`] = [
                requiredWhen({
                    when: () =>
                        (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                        ["02", "18", "19"].some(d => props.requestDocKeys.includes(d))
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.nature`] = [
                requiredWhen({
                    when: () => ["02"].some(d => props.requestDocKeys.includes(d))
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.formula`] = [
                requiredWhen({
                    when: () =>
                        (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                        ["02", "18", "19"].some(d => props.requestDocKeys.includes(d))
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.refCoordinateCustoms`] = [required];
            rules[`${prefix}refInformTemp.products.${index}.manufacturer`] = [
                requiredWhen({
                    when: () =>
                        (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                        ["02", "18", "19"].some(d => props.requestDocKeys.includes(d))
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.tradeName`] = [
                requiredWhen({
                    when: () => (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                        ["02", "18", "19"].some(d => props.requestDocKeys.includes(d))
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.storageName`] = [
                requiredWhen({
                    when: () => ["02"].some(d => props.requestDocKeys.includes(d))
                })
            ];
            rules[`${prefix}refInformTemp.products.${index}.refStorageLocation`] = [
                requiredWhen({
                    when: () => (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1")
                })
            ];

            // ไม่เช็คโควต้าคำขอยกเลิก
            if (model.refRequestType?.key !== '4') {
                rules[`${prefix}refInformTemp.products.${index}.quantityRemaining`] = [
                    requiredQuantityRemaining({ initInform: initModel.refInformTemp, informProduct: item })
                ];
            }
            /*
            item.addresses?.forEach((address, index2) => {
                if (address.isDelete !== true) {
                    rules[`${prefix}refInformTemp.products.${index}.addresses.${index2}.noTh`] = [
                        requiredWhen({
                            when: () =>
                                (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                                ["02"].some(d => props.requestDocKeys.includes(d))
                        })
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.addresses.${index2}.refProvince`] = [
                        requiredWhen({
                            when: () =>
                                (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                                ["02"].some(d => props.requestDocKeys.includes(d))
                        })
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.addresses.${index2}.refAmphur`] = [
                        requiredWhen({
                            when: () =>
                                (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                                ["02"].some(d => props.requestDocKeys.includes(d))
                        })
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.addresses.${index2}.refTambon`] = [
                        requiredWhen({
                            when: () =>
                                (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                                ["02"].some(d => props.requestDocKeys.includes(d))
                        })
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.addresses.${index2}.postCode`] = [
                        requiredWhen({
                            when: () =>
                                (["16", "17"].some(d => props.requestDocKeys.includes(d)) && item.refHazSpecies?.key === "1") ||
                                ["02"].some(d => props.requestDocKeys.includes(d))
                        })
                    ];
                }
            });
            */
            rules[`${prefix}refInformTemp.products.${index}.productQties`] = [
                {
                    validator: (rule, value) => {
                        if (value === undefined || value === null || value?.length === 0 || value?.toString()?.length === 0) {
                            return Promise.reject(i18n.global.t("error.form.requiredLeastOne"));
                        }
                        return Promise.resolve();
                    },
                    required: true,
                    trigger: "change"
                }
            ];
            item.productQties?.forEach((productQty, index2) => {
                if (productQty.isDelete !== true) {
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.refStorageLocation`] = [
                        requiredWhen({
                            when: () => ["16", "17"].some(d => props.requestDocKeys.includes(d)) && ["2", "3"].includes(item.refHazSpecies?.key)
                        })
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.refStorageLocationSub`] = [
                        requiredWhen({
                            when: () => ["16", "17"].some(d => props.requestDocKeys.includes(d)) && ["2", "3"].includes(item.refHazSpecies?.key)
                        })
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.refStorageLocationSubItem`] = [
                        requiredWhen({
                            when: () => ["17"].includes(model.refRequestDoc?.key) && ["2", "3"].includes(item.refHazSpecies?.key)
                        })
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.qty`] = [required];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.refQtyUnit`] = [required];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.netWeight`] = [
                        {
                            validator: (rule, value) => {
                                const initInformProduct = initModel.refInformTemp.products?.find(d => d.id === item.id);
                                const initProductQty = initInformProduct?.productQties?.find(d => d.id === productQty.id);
                                if (["16", "17"].some(d => props.requestDocKeys.includes(d)) && ["2", "3"].includes(item.refHazSpecies?.key)) {
                                    return requiredNetWeight({
                                        data,
                                        request: model,
                                        inform: model.refInformTemp,
                                        initInform: initModel.refInformTemp,
                                        informProduct: item,
                                        initInformProduct,
                                        productQty,
                                        initProductQty
                                    }).validator(rule, value);
                                }
                                return required.validator(rule, value);
                            },
                            trigger: "change"
                        }
                    ];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.refNetWeightUnit`] = [required];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.refPackagingCharacteristic`] = [required];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.numberOfPackage`] = [required];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.packageSize`] = [required];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.refPackageUnit`] = [required];
                    rules[`${prefix}refInformTemp.products.${index}.productQties.${index2}.amount`] = [required];
                }
            });
        }
    });
    return rules;
};

export const createRulesTab5 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refInformTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refInformTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refInformTemp.attachments.${index}.size`] = [
                requiredWhen({
                    when: () =>
                        model.refInformTemp.attachments[index].attribute?.isRequiredAttachment ||
                        model.refInformTemp.attachments[index].attribute?.documentNo
                })
            ];
        }
    });
    return rules;
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault, requestDocKeys } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        ["16", "17"].some(d => requestDocKeys.includes(d))
            ? callInformAttachment({})
            : ["02"].some(d => requestDocKeys.includes(d))
            ? callImportSampleAttachment({})
            : ["18", "19"].some(d => requestDocKeys.includes(d))
            ? callPassAttachment({})
            : Promise.resolve([]),
        callRequestStatus({}),
        callRequestType({}),
        callRequestDoc({}),
        ["16", "17", "02"].some(d => requestDocKeys.includes(d))
            ? callUserObjective({})
            : ["18", "19"].some(d => requestDocKeys.includes(d))
            ? callUserObjective3({})
            : Promise.resolve([]),
        callInformProductQtyMargin({}),
        callRequestDocGenType({})
    ];
    return Promise.all(promises).then(arr => {
        data.informAttachments = arr[0];
        data.statuses = arr[1];
        data.requestTypes = arr[2];
        data.requestDocs = arr[3];
        data.userObjectives = arr[4];
        data.informProductQtyMargins = arr[5];
        data.requestDocGenTypes = arr[6];
        const userInfo = store.state.resource.auth.info?.user;

        if ((!id && !form) || forceDefault) {
            modelRef.isOfficerCreate = !!userInfo.refOfficerLv;
            modelRef.lastStatus.draft[0].statusDatetime = moment().format("YYYY-MM-DD[T]HH:mm:ssZ");
            data.informAttachments.forEach(item =>
                modelRef.refInformTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
            modelRef.refRequestType = data.requestTypes.find(d => d.key === "1");
            modelRef.refRequestDoc = data.requestDocs.find(d => d.key === requestDocKeys?.[0]);
            modelRef.refInformTemp.refRequestDoc = data.requestDocs.find(d => d.key === requestDocKeys?.[0]);
            modelRef.refDocGenType = data.requestDocGenTypes.find(d => d.key === "1");
            if (!modelRef.isOfficerCreate) {
                callUserCompanyMe({}).then(userCompanies => {
                    const refUserCompany = userCompanies[0];
                    modelRef.refInformTemp.refUserCompany = refUserCompany;
                    if (refUserCompany) {
                        callUser({ userCompanyId: refUserCompany.id }).then(
                            users => (modelRef.refInformTemp.refUser = users.find(d => d.id === store.state.resource.auth.info?.user?.id))
                        );
                        if (["16", "17", "18", "19"].includes(modelRef.refRequestDoc?.key)) {
                            modelRef.refInformTemp.refOperation = checkUserCompanyOperation({
                                operations: data.userObjectives,
                                userCompany: refUserCompany
                            })[0];
                        } else if (["02"].includes(modelRef.refRequestDoc?.key)) {
                            modelRef.refInformTemp.refOperation = data.userObjectives.find(d => d.key === "1");
                        }
                        data.onUpdateRefOperation?.(modelRef.refInformTemp.refOperation);
                    }
                });
            }
        }
    });
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const createAppPageHeaderDefault = ({ props, data, refs, state }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix, requestDocKeys } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive, feeStatements } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canWaitReceive = state.conditionAction.canWaitReceive({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canSendNsw = state.conditionAction.canSendNsw({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendReceive = false;
                    const refInformTemp = modelRef.refInformTemp;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forReceiveRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        state,
                        formParams: {
                            refInformTemp: {
                                ...modelRef.refInformTemp,
                                refUserCompany: refInformTemp?.refUserCompany?.id ? refInformTemp?.refUserCompany : null,
                                refUser: refInformTemp?.refUser?.id ? refInformTemp?.refUser : null
                            },
                            statusRemark
                        }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canWaitReceive) {
        const statusWaitReceive = data.statuses.find(status => status.key === "waitReceive");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitReceive?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitReceive?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmRequest({
                            onOk: record => {
                                const refInformTemp = modelRef.refInformTemp;
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    state,
                                    formParams: {
                                        refInformTemp: {
                                            ...modelRef.refInformTemp,
                                            refUserCompany: refInformTemp?.refUserCompany?.id ? refInformTemp?.refUserCompany : null,
                                            refUser: refInformTemp?.refUser?.id ? refInformTemp?.refUser : null
                                        },
                                        statusRemark: modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: modelRef.lastStatus.forRequestRemark[0].remark,
                                        refStatus: statusWaitReceive,
                                        isSignCa: record.isSignCa,
                                        passwordCa: record.passwordCa
                                    },
                                    successText: i18n.global.t("views.request.modal.request.sendSuccess")
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        callInspectionLv({}).then(inspectionLvs => {
                            openModalFormConfirmReceive({
                                props: {
                                    defaultRefInspectionLv: ["16", "17"].some(d => requestDocKeys.includes(d))
                                        ? inspectionLvs.find(d => d.key === "3")
                                        : inspectionLvs.find(d => d.key === "3"),
                                    roleName: ["16", "17"].some(d => requestDocKeys.includes(d))
                                        ? "INFORM_APPROVE"
                                        : ["02"].some(d => requestDocKeys.includes(d))
                                        ? "IMPORT_SAMPLE_APPROVE"
                                        : ["18", "19"].some(d => requestDocKeys.includes(d))
                                        ? "PASS_APPROVE"
                                        : "",
                                    statusKeys: ["draft"].includes(initialModel.refStatus?.key) ? ["waitApprove"] : [],
                                    disabledRefInspectionLv: ["18", "19"].some(d => requestDocKeys.includes(d)) ? true : false,
                                    visibleReEditNsw: !!modelRef.refInformTemp.eformCode
                                },
                                onOk: record => {
                                    const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                    onClickSave({
                                        props,
                                        data,
                                        refs,
                                        state,
                                        formParams: {
                                            statusRemark: isStatusBack
                                                ? modelRef.lastStatus.forRequestRemark[0].remark
                                                : modelRef.lastStatus.forReceiveRemark[0].remark,
                                            statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forReceiveRemark[0].remark : null,
                                            refStatus: record.refStatus,
                                            refToApproveBy: record.refToApproveBy,
                                            refInformTemp: { ...modelRef.refInformTemp, refInspectionLv: record.refInspectionLv }
                                        },
                                        successText: i18n.global.t(`views.request.modal.receive.${record.refStatus?.key}`)
                                    });
                                }
                            });
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmApprove({
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    state,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forReceiveRemark[0].remark : null,
                                        refStatus: record.refStatus
                                    },
                                    successText: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canSendNsw) {
        col2.push(
            createButton({
                text: i18n.global.t("button.sendNsw"),
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    Modal.confirm({
                        title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.sendNsw") }),
                        autoFocusButton: "cancel",
                        content: <div>{i18n.global.t("button.sendNsw")}</div>,
                        onOk: () => {
                            callSendNsw({ data: modelRef }).then(() => {
                                if (viewOnly) initForm({ props, data, refs, state });
                                else onClickView({ record: modelRef, routerPrefix });
                            });
                        },
                    });
                }
            })
        );
    }
    if (id && !modelRef.isNswProcess && modelRef.nswLicenseResponseMessageType) {
        col2.push(
            createButton({
                text: `${i18n.global.t("button.nswLicenseResponse")} (${modelRef.nswLicenseResponseMessageType})`,
                classStyle: `m-1 ${modelRef.nswLicenseResponseMessageType === "ACCEPT" ? "btn-success" : "btn-danger"}`,
                onClick: () => {
                    Modal.info({
                        title: `${i18n.global.t("button.nswLicenseResponse")} (${modelRef.nswLicenseResponseMessageType}) - ${modelRef.nswLicenseResponseId})`,
                        autoFocusButton: "cancel",
                        width: 1200,
                        content: <pre lang="xml">{formatXml(modelRef.nswLicenseResponsePayload, "   ")}</pre>,
                    });
                }
            })
        )
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    if (
        id &&
        feeStatements?.[0]?.id &&
        ((has({ menuContext, key: "forRequest" }) && ![undefined, "draft"].includes(feeStatements?.[0]?.refStatus?.key)) ||
            have({ menuContext, keys: ["forReceive", "forApprove"] }))
    ) {
        col2.push(
            createButton({
                text: `${i18n.global.t("model.feeStatement.modelName")}${feeStatements?.[0] ? ` (${feeStatements[0].refStatus?.value})` : ""}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    validate({ props, data, refs }).then(() => {
                        router.push({ name: `fees-view`, params: { id: feeStatements?.[0]?.id } });
                    });
                },
                icon: <CIcon file="active.svg"></CIcon>
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const initForm = ({ props, data, refs, state }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    data.userInfo = store.state.resource.auth.info?.user;
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => ({
            length1: data.useForm.modelRef.refInformTemp.products.filter(d => d.isDelete !== true).length,
            length2: data.useForm.modelRef.refInformTemp.products
                .filter(d => d.isDelete !== true)
                .flatMap(d => d.addresses.filter(d => d.isDelete !== true)).length,
            length3: data.useForm.modelRef.refInformTemp.products
                .filter(d => d.isDelete !== true)
                .flatMap(d => d.productQties.filter(d => d.isDelete !== true)).length
        }),
        createRules: () => createRulesTab4({ props, data, refs }),
        itemsKey: "refInformTemp.products"
    });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refInformTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab5({ props, data, refs }),
        itemsKey: "refInformTemp.attachments"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs, state })
                }
            });
        }
    });
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix, requestDocKeys } = props;
    const stateKey = `${routerPrefix}|${requestDocKeys.join(", ")}`;
    if (stateGroup[stateKey]) {
        return stateGroup[stateKey];
    }
    const state = {
        columns: createColumns({ props }),
        page: createPage({
            options: {
                select: [
                    "*",
                    "refInform.*",
                    "refInformTemp.*",
                    "refInformTemp.refUserCompany.*",
                    "refInformTemp.refUserCompany.refPersonType.*",
                    "refInformTemp.refUser.*",
                    "refInformTemp.refOperation.*",
                    "refRequestType.*",
                    "refDepartment.*",
                    "refStatus.*"
                ],
                sort: [/* { name: "refStatus.seq", type: "ASC" } ,*/{ name: "updateDate", type: "DESC" }],
                children: [
                    {
                        name: "refInformTemp.products",
                        select: ["*"],
                        page: 0,
                        size: 1000000
                    }
                ]
            }
        }),
        tableOptions: { search: { activeKey: "Search" } },
        conditionAction: createConditionAction()
    };
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        createRowActionReStatus({
            menuContext,
            onClick: ({ record }) => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        setIsLoadingApp(true);
                        callReStatus({ data: record })
                            .finally(() => setIsLoadingApp(false))
                            .then(() => onSearch({ props, state, page: state.page }));
                    }
                });
            },
            visible: ({ record }) => state.conditionAction.canReStatus({ props, record })
        }),
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callReport: ({ record }) => callReportInform({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/company/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`,
            visible: ({ record }) => state.conditionAction.canReport({ props, record })
        })
    ];
    return (stateGroup[stateKey] = state);
};

export const stateGroup = [];
